import {
  AvatarCustomer,
  AvatarProps,
  FormAutocompleteField,
  FormAutocompleteFieldProps,
} from '@razz-team/ui-web';
import { useCallback, useMemo } from 'react';
import { useBusinessMembersQuery } from '../../api/members';
import { MemberListItem } from '../pages/BusinessPage/Member/MemberListItem';

export type CustomerFieldProps<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
> = Omit<
  FormAutocompleteFieldProps<string, Multiple, DisableClearable>,
  'options' | 'getOptionLabel'
>;

export function MemberField<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
>(props: CustomerFieldProps<Multiple, DisableClearable>) {
  const membersQuery = useBusinessMembersQuery();
  const members = useMemo(() => membersQuery.data?.members ?? [], [membersQuery.data]);
  const options = useMemo(() => members.map(({ customer }) => customer.id), [members]);

  const getOptionLabel = useCallback(
    (option: string) => {
      const member = members.find(({ customer }) => customer.id === option);
      if (member) {
        return (
          member.customer.name ??
          member.identity.email ??
          member.identity.unconfirmedEmail ??
          member.customer.username ??
          member.customer.id
        );
      }

      return option;
    },
    [members],
  );

  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: string) => {
      const member = members.find(({ customer }) => customer.id === option);
      return member ? <MemberListItem {...optionProps} key={option} member={member} /> : null;
    },
    [members],
  );

  const renderAvatar = useCallback(
    (option: string, avatarProps: Partial<AvatarProps>) => {
      const member = members.find(({ customer }) => customer.id === option);
      return member ? <AvatarCustomer customer={member.customer} {...avatarProps} /> : null;
    },
    [members],
  );

  return (
    <FormAutocompleteField<string, Multiple, DisableClearable>
      options={options}
      loading={membersQuery.isPending}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderAvatar={renderAvatar}
      {...props}
    />
  );
}
